import React from 'react';
import { CheckCircle, Sliders, Loader2, ArrowRight, X } from 'lucide-react';

const CommitBar = ({ plan, onCommit, onOpenOverrides, isLoading }) => {
    const isCommitted = plan?.status === 'COMMITTED';
    const [isVisible, setIsVisible] = React.useState(!isCommitted);

    // Auto-dismiss logic for COMMITTED state
    React.useEffect(() => {
        if (isCommitted) {
            const timer = setTimeout(() => {
                setIsVisible(false);
            }, 3000); // 3 seconds
            return () => clearTimeout(timer);
        } else {
            setIsVisible(true); // Always show if not committed (e.g. DRAFT)
        }
    }, [isCommitted]);

    if (!plan || plan.meals.length === 0 || !isVisible) return null;

    return (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] w-[95%] max-w-2xl animate-in slide-in-from-bottom-10 fade-in duration-500">
            <div className={`glass-panel rounded-3xl p-4 flex items-center justify-between gap-4 shadow-2xl transition-all duration-500 ${isCommitted ? 'border-emerald-500/30' : 'border-[var(--accent-color)]/30'}`}>
                {/* Status Indicator */}
                <div className="flex items-center gap-3 px-2">
                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center transition-all duration-500 ${isCommitted ? 'bg-emerald-500 text-white' : 'bg-[var(--accent-color)] text-white shadow-lg shadow-blue-500/20'}`}>
                        {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : isCommitted ? <CheckCircle className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
                    </div>
                    <div>
                        <div className="text-[10px] font-black uppercase tracking-[0.2em] opacity-50">Meal Plan Status</div>
                        <div className={`text-sm font-black ${isCommitted ? 'text-emerald-500' : 'text-[var(--text-primary)]'}`}>
                            {isCommitted ? 'PLAN FINALIZED' : 'DRAFT MODE'}
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                    {!isCommitted && (
                        <>
                            <button
                                onClick={onOpenOverrides}
                                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold text-[var(--text-secondary)] hover:bg-[var(--text-secondary)]/10 transition-all active:scale-95"
                            >
                                <Sliders className="w-4 h-4" />
                                <span className="hidden sm:inline">Modify Quantities</span>
                            </button>
                            <button
                                onClick={onCommit}
                                disabled={isLoading}
                                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[var(--accent-color)] hover:brightness-110 text-white text-xs font-black uppercase tracking-widest shadow-lg shadow-blue-500/20 transition-all active:scale-95 disabled:opacity-50"
                            >
                                Commit to Plan
                            </button>
                        </>
                    )}
                    {isCommitted && (
                        <div className="flex items-center gap-2">
                            <div className="px-4 py-2.5 rounded-xl bg-emerald-500/10 text-emerald-500 text-xs font-black uppercase tracking-widest flex items-center gap-2 border border-emerald-500/20">
                                <CheckCircle className="w-4 h-4" />
                                Inventory Deducted
                            </div>
                            <button
                                onClick={() => setIsVisible(false)}
                                className="p-2 rounded-xl hover:bg-emerald-500/10 text-emerald-500/50 hover:text-emerald-500 transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Contextual Tip */}
            {!isCommitted && (
                <div className="mt-3 text-center">
                    <p className="text-[10px] font-bold text-[var(--text-secondary)] opacity-60">
                        Committing will subtract ingredients from your digital kitchen and enable PDF export.
                    </p>
                </div>
            )}
        </div>
    );
};

export default CommitBar;
